import { apiClient } from './apiClient';
import { ChatMessage, Conversation, PaginatedResponse, SendMessagePayload } from '../types';

export const chatService = {
  // ─── Negotiation-based messages (legacy) ─────────────
  async getHistory(
    negotiationId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<ChatMessage>> {
    const { data } = await apiClient.get<PaginatedResponse<ChatMessage>>(
      `/negotiations/${negotiationId}/messages/`,
      { params },
    );
    return data;
  },

  async sendMessage(payload: SendMessagePayload): Promise<ChatMessage> {
    const { data } = await apiClient.post<ChatMessage>(
      `/negotiations/${payload.negotiation_id}/messages/`,
      { content: payload.content, reply_to: payload.reply_to },
    );
    return data;
  },

  async getNegotiation(negotiationId: string) {
    const { data } = await apiClient.get(`/negotiations/${negotiationId}/`);
    return data;
  },

  async markNegotiationRead(negotiationId: string): Promise<void> {
    await apiClient.post(`/negotiations/${negotiationId}/read/`);
  },

  async listNegotiations(params?: {
    status?: string;
    page?: number;
  }) {
    const { data } = await apiClient.get('/negotiations/', { params });
    return data;
  },

  // ─── Conversations (inbox) ───────────────────────────
  async listConversations(params?: {
    type?: 'REQUEST' | 'CLIENT_SUPPORT' | 'SURVEYOR_SUPPORT';
    is_active?: boolean;
    page?: number;
  }): Promise<PaginatedResponse<Conversation>> {
    const { data } = await apiClient.get<PaginatedResponse<Conversation>>(
      '/conversations/',
      { params },
    );
    return data;
  },

  async getConversation(conversationId: string): Promise<Conversation> {
    const { data } = await apiClient.get<Conversation>(
      `/conversations/${conversationId}/`,
    );
    return data;
  },

  async getConversationMessages(
    conversationId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<PaginatedResponse<ChatMessage>> {
    const { data } = await apiClient.get<PaginatedResponse<ChatMessage>>(
      `/conversations/${conversationId}/messages/`,
      { params },
    );
    return data;
  },

  async sendConversationMessage(
    conversationId: string,
    content: string,
    replyToId?: string,
  ): Promise<ChatMessage> {
    const { data } = await apiClient.post<ChatMessage>(
      `/conversations/${conversationId}/messages/`,
      { content, reply_to: replyToId },
    );
    return data;
  },

  async markConversationRead(conversationId: string, messageIds?: string[]): Promise<void> {
    await apiClient.post(`/conversations/${conversationId}/read/`, {
      message_ids: messageIds,
    });
  },

  async blockConversation(
    conversationId: string,
    reason: string,
  ): Promise<Conversation> {
    const { data } = await apiClient.post<Conversation>(
      `/conversations/${conversationId}/block/`,
      { reason },
    );
    return data;
  },

  async unblockConversation(conversationId: string): Promise<Conversation> {
    const { data } = await apiClient.post<Conversation>(
      `/conversations/${conversationId}/unblock/`,
    );
    return data;
  },

  async removeMessage(
    conversationId: string,
    messageId: string,
  ): Promise<void> {
    await apiClient.delete(`/conversations/${conversationId}/messages/${messageId}/`);
  },
};
